import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useInventoryMovements } from '@/hooks/useInventoryMovements';

const TYPE_MAP: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  ENTRY: { label: 'Entrada', color: 'text-emerald-700', bg: 'bg-emerald-50', icon: 'ri-arrow-down-circle-line' },
  PURCHASE: { label: 'Compra', color: 'text-emerald-700', bg: 'bg-emerald-50', icon: 'ri-shopping-cart-2-line' },
  SALE: { label: 'Venta', color: 'text-accent-700', bg: 'bg-accent-50', icon: 'ri-shopping-bag-3-line' },
  RESERVE: { label: 'Reserva', color: 'text-amber-700', bg: 'bg-amber-50', icon: 'ri-lock-line' },
  RELEASE: { label: 'Liberación', color: 'text-cyan-700', bg: 'bg-cyan-50', icon: 'ri-lock-unlock-line' },
  CONSIGNMENT_OUT: { label: 'Consignación Salida', color: 'text-indigo-700', bg: 'bg-indigo-50', icon: 'ri-send-plane-line' },
  CONSIGNMENT_SOLD: { label: 'Consignación Vendida', color: 'text-emerald-700', bg: 'bg-emerald-50', icon: 'ri-check-double-line' },
  CONSIGNMENT_RETURN: { label: 'Consignación Retorno', color: 'text-indigo-700', bg: 'bg-indigo-50', icon: 'ri-arrow-go-back-line' },
  RETURN: { label: 'Devolución', color: 'text-orange-700', bg: 'bg-orange-50', icon: 'ri-arrow-go-back-line' },
  EXCHANGE_OUT: { label: 'Cambio Salida', color: 'text-purple-700', bg: 'bg-purple-50', icon: 'ri-arrow-left-right-line' },
  EXCHANGE_IN: { label: 'Cambio Entrada', color: 'text-purple-700', bg: 'bg-purple-50', icon: 'ri-arrow-left-right-line' },
  ADJUSTMENT: { label: 'Ajuste', color: 'text-foreground-700', bg: 'bg-background-100', icon: 'ri-equalizer-line' },
  LOSS: { label: 'Merma', color: 'text-red-700', bg: 'bg-red-50', icon: 'ri-close-circle-line' },
};

export default function InventoryMovementsPage() {
  const { movements, loading, error, refetch } = useInventoryMovements();
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const types = ['all', ...Array.from(new Set(movements.map((m) => m.movementType)))];

  const filtered = movements.filter((m) => {
    if (typeFilter !== 'all' && m.movementType !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !m.productName.toLowerCase().includes(q) &&
        !m.sku.toLowerCase().includes(q) &&
        !m.reason?.toLowerCase().includes(q)
      ) {
        return false;
      }
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Movimientos de Inventario</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{movements.length} movimientos registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
        </div>

        <div className="bg-accent-50 border border-accent-200 rounded-xl p-4 flex items-start gap-3">
          <i className="ri-information-line text-accent-600 text-lg mt-0.5"></i>
          <p className="text-sm text-accent-800">
            Historial inmutable de cambios de stock. Cada movimiento registra el snapshot{' '}
            <strong>prev_available &rarr; new_available</strong>, garantizando trazabilidad total de la regla de oro del inventario.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex flex-wrap gap-1.5">
            {types.map((t) => {
              const meta = TYPE_MAP[t] || { label: t, color: 'text-foreground-600', bg: 'bg-background-100' };
              return (
                <button
                  key={t}
                  onClick={() => setTypeFilter(t)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    typeFilter === t
                      ? 'bg-accent-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {t === 'all' ? 'Todos' : meta.label}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 max-w-xs ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por producto, SKU o motivo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-300"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Fecha</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Tipo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Producto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Cantidad</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-center">Snapshot Disponible</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Costo Unit.</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando movimientos...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((m) => {
                    const meta = TYPE_MAP[m.movementType] || {
                      label: m.movementType,
                      color: 'text-foreground-600',
                      bg: 'bg-background-100',
                      icon: 'ri-git-commit-line',
                    };
                    const delta = m.newAvailable - m.prevAvailable;
                    return (
                      <tr key={m.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">
                          {m.createdAt
                            ? new Date(m.createdAt).toLocaleString('es-CO', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.color} ${meta.bg}`}>
                            <i className={`${meta.icon} text-xs`}></i>
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <p className="text-foreground-900 font-medium text-sm">{m.productName}</p>
                          <p className="text-xs text-foreground-500">
                            {m.sku}
                            {m.sizeName ? ` · ${m.sizeName}` : ''}
                          </p>
                        </td>
                        <td className={`px-5 py-3 text-right font-medium whitespace-nowrap ${delta < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                          {delta > 0 ? '+' : ''}
                          {m.quantity}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-center gap-1.5">
                            <span className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-600 font-mono">
                              {m.prevAvailable}
                            </span>
                            <i className="ri-arrow-right-line text-foreground-400 text-xs"></i>
                            <span className="text-xs px-2 py-0.5 rounded bg-accent-100 text-accent-700 font-mono font-medium">
                              {m.newAvailable}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">
                          ${m.unitCost.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-500 hidden lg:table-cell max-w-[240px] truncate">
                          {m.reason || '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {movements.length === 0
                  ? 'Aún no hay movimientos. Se generarán al confirmar ventas o registrar entradas.'
                  : 'No se encontraron movimientos con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}